/* eslint-disable no-prototype-builtins */
import React, {useEffect, useMemo, useState} from 'react';
import {Box, Divider, Typography} from '@mui/material';
import AurBreadcrumb from '../../common/aur-breadcrumb/AurBreadCrumb';
import {t} from 'i18next';
import {ReactComponent as CircleArrow} from '../../../assests/svg/circle-arrow.svg';
import {ReactComponent as BackgroundBlur} from '../../../assests/svg/backgroundBlur.svg';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {getEcaFields, getExportDetailsView} from '../../../api/EcaServices';
import {formatFileSize, getUTCDateTimeForDisplay} from '../../../utils/utils';
import {getTagData} from '../../../api/EcaManagementService';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import {AurLightTooltip} from '../../common/aur-light-tooltip/aur-light-tooltip';
import {EciHomePageTabs} from '../../../constants/constants';

interface ExportDetailsType {
  notes: any;
  ecaExportId: number;
  errorMessage: string;
  exportCustomFields: ExportCustomField[];
  exportDateTime: string;
  exportFileType: string;
  exportId: string;
  exportName: string;
  exportType: string;
  exportedBy: string;
  filePath: string;
  fileSize: number;
  notificationEmail: string;
  status: string;
  tagFieldName: string;
  tagUsed: string;
  tagValueId: number;
  tagValueLabel: string;
  tagFieldId: number;
  totalDocExported: number;
  promotionBatchIds: string;
}

interface ExportCustomField {
  fieldName: string;
  fieldLabel: string;
}

interface ITags {
  id: string;
  label: string;
  name: string;
  values: any[];
}

const ExportViewDetails: React.FC = () => {
  const exportId = useSelector(
    (state: RootState) => state?.addExport?.exportId
  );
  const exportName = useSelector(
    (state: RootState) => state?.addExport?.exportName
  );
  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;

  const {projectId} = useSelector((state: RootState) => state.reports);
  const [selectedTagData, setSelectedTagData] = useState<ITags[]>([]);
  const [reqLoading, setReqLoading] = useState<boolean>(false);
  const [exportDetails, setExportDetails] = useState<ExportDetailsType | null>(
    null
  );
  const [fieldData, setFieldData] = useState<any[]>([]);

  const breadCrumbItems = useMemo(() => {
    const baseItems: {label: string; path?: string}[] = [
      {
        label: t('eca.ecaHomepage.coreEciHome'),
        path: `/eci-home-page?tab=${EciHomePageTabs.ECI_OVERVIEW}`,
      },
      {
        label: t('eca.exportManagement.manageExports'),
        path: '/eci-home-page/export-management',
      },
      {
        label: `${exportName}`,
      },
    ];
    return baseItems;
  }, []);

  const handleExportDetailsView = async () => {
    try {
      const params = `clientId=${clientId}&projectId=${projectId}&`;
      const response = await getExportDetailsView(exportId, params);
      setExportDetails(response?.data?.data);
    } catch (error: any) {
      console.error(error?.message);
    }
  };

  const getTagDropDown = async () => {
    const params = `?clientId=${clientId}&projectId=${projectId}`;
    setReqLoading(true);
    try {
      const results = await getTagData(params);
      if (results.status == 200 || results.status == 201) {
        const tags = results?.data?.data;
        setSelectedTagData(tags);
      }
    } catch (e) {
      console.log(e);
      setReqLoading(false);
    } finally {
      setReqLoading(false);
    }
  };
  const fetchAvailableField = async () => {
    const params = `?clientId=${clientId}&projectId=${projectId}&skip=0&top=3000`;
    try {
      const results = await getEcaFields(params);
      const dataField = results?.data?.data;
      setFieldData(dataField);
    } catch (e) {
      console.log(e);
    }
  };
  useEffect(() => {
    handleExportDetailsView();
    getTagDropDown();
    fetchAvailableField();
    window.scrollTo({
      top: 0,
      behavior: 'auto',
    });
  }, []);

  const tagFieldObj = selectedTagData?.find(
    (field) => String(field.id) === String(exportDetails?.tagFieldId)
  );

  const exportStatusClass = useMemo(() => {
    if (exportDetails?.status === 'InProgress') return 'inprogress-color';
    if (exportDetails?.status === 'Completed') return 'completed-color';
    if (exportDetails?.status === 'Pending') return 'light-blue';
    return 'error-color';
  }, [exportDetails?.status]);

  const exportTypeLabel = useMemo(() => {
    switch (exportDetails?.exportType) {
      case 'Review':
        return t('eca.exportManagement.exportedForReview');
      case 'Analytics':
        return t('eca.exportManagement.exportedForAnalytics');
      default:
        return exportDetails?.exportType;
    }
  }, [exportDetails?.exportType]);

  const tagFieldLabel = tagFieldObj?.label;
  return (
    <div className="export-view-details-container">
      <BackgroundBlur className="top-right-background" aria-hidden="true" />

      <Box>
        <Box
          className="breadcrumb-section overlay-breadcrumb"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <AurBreadcrumb items={breadCrumbItems} />
        </Box>
      </Box>
      <Box className="export-view-body">
        <Box className="export-heading-container">
          <Box className="no-background">
            <Box className="temperory">
              <Box className="header-status">
                <Typography
                  className="typo-text display3_size export-name-heading "
                  tabIndex={0}
                >
                  {exportDetails?.exportName}
                </Typography>
              </Box>
              <Box className="export-props">
                <Box className="id-container" tabIndex={0}>
                  <Typography className="typo-text label2_size export-light-blue">
                    {t('eca.exportManagement.exportIds')}
                  </Typography>
                  <Typography className="typo-text label2_size export-white">
                    &nbsp;{exportDetails?.ecaExportId}
                  </Typography>
                </Box>
                <Box className="id-container" tabIndex={0}>
                  <Typography className="typo-text label2_size export-light-blue">
                    {t('eca.exportManagement.exportedBy')}:
                  </Typography>
                  <Typography className="typo-text label2_size export-white">
                    &nbsp;{exportDetails?.exportedBy} {t('common.on')}{' '}
                    <AurLightTooltip title={t('common.yearMonthDate')}>
                      <span>
                        {getUTCDateTimeForDisplay(
                          exportDetails?.exportDateTime ?? new Date()
                        )}
                      </span>
                    </AurLightTooltip>
                  </Typography>
                </Box>
              </Box>
              <Box className="export-props">
                <Box className="id-container" tabIndex={0}>
                  <Typography className="typo-text label2_size export-light-blue">
                    {t('eca.exportManagement.promotionBatchIds')}
                  </Typography>
                  <Typography className="typo-text label2_size export-white">
                    &nbsp;{exportDetails?.promotionBatchIds}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box className="status-details">
              <Typography
                className="typo-text label2_size export-light-blue"
                tabIndex={0}
              >
                {t('eca.exportManagement.completedOn')}{' '}
                <AurLightTooltip title={t('common.yearMonthDate')}>
                  <span>
                    {' '}
                    {getUTCDateTimeForDisplay(
                      exportDetails?.exportDateTime ?? new Date()
                    )}
                  </span>
                </AurLightTooltip>
              </Typography>
              <Typography
                className={`typo-text subhead3_size export-white ${exportStatusClass}`}
                tabIndex={0}
              >
                {!reqLoading &&
                  t(
                    `eca.exportManagement.exportStatus.${exportDetails?.status}`
                  )}
              </Typography>
            </Box>
          </Box>
          <Box className="doc-count-container">
            <Box className="exported-docs" tabIndex={0}>
              <Typography className="typo-text body2_size export-light-blue">
                {t('eca.exportManagement.docsExported')}
              </Typography>
              <Typography className="typo-text display3_size export-white">
                {exportDetails?.hasOwnProperty('totalDocExported')
                  ? `${exportDetails.totalDocExported}`
                  : '- -'}
              </Typography>
            </Box>
            <Divider
              orientation="vertical"
              flexItem
              sx={{bgcolor: '#ffffff40', width: '1px'}}
            ></Divider>
            <CircleArrow className="circle-arrow-icon" />
            <Box className="total-volume" tabIndex={0}>
              <Typography className="typo-text body2_size export-light-blue">
                {t('eca.exportManagement.totalVolume')}
              </Typography>
              <Typography className="typo-text display3_size export-white">
                {exportDetails?.hasOwnProperty('fileSize')
                  ? formatFileSize(exportDetails?.fileSize)
                  : '- -'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{position: 'relative', left: 0, right: 0}}>
            <Divider
              orientation="horizontal"
              flexItem
              sx={{marginTop: '24px', bgcolor: '#263449', height: '1px'}}
            ></Divider>
          </Box>
        </Box>
        <Typography
          className="typo-text headline3_size review-text export-white"
          tabIndex={0}
          component="h1"
        >
          {t('eca.exportManagement.reviewExportSettings')}
        </Typography>
        <Box className="export-review-container">
          <Box className="setup-details-title">
            <Typography
              className="typo-text label2_size subhead-review-text export-light-blue"
              tabIndex={0}
            >
              {t('eca.exportManagement.setupDetails')}
            </Typography>
          </Box>
          <Box className="setup-details">
            <Box className="setup-details-record" tabIndex={0}>
              <Typography className="typo-text subhead4_size details-key export-white">
                {t('eca.exportManagement.exportType')}
              </Typography>
              <Typography className="typo-text body2_size details-value export-value-light">
                {exportTypeLabel}
              </Typography>
            </Box>
            <Box className="setup-details-record" tabIndex={0}>
              <Typography className="typo-text subhead4_size details-key export-white">
                {t('eca.exportManagement.tagField')}
              </Typography>
              <Typography className="typo-text body2_size details-value export-value-light">
                {tagFieldLabel}, {exportDetails?.tagUsed}{' '}
              </Typography>
            </Box>
            <Box className="setup-details-record" tabIndex={0}>
              <Typography className="typo-text subhead4_size details-key export-white">
                {t('eca.exportManagement.exportedTo')}
              </Typography>
              {exportDetails?.exportType === 'Analytics' && (
                <Typography className="typo-text body2_size details-value export-value-light">
                  {t('eca.exportManagement.exportedToValue')}
                </Typography>
              )}
            </Box>
            <Box className="setup-details-record" tabIndex={0}>
              <Typography className="typo-text subhead4_size details-key export-white">
                {t('eca.exportManagement.exportedPath')}
              </Typography>
              <Typography className="typo-text body2_size details-value export-value-light">
                {exportDetails?.filePath}
              </Typography>
            </Box>
            <Box className="setup-details-record" tabIndex={0}>
              <Typography className="typo-text subhead4_size details-key export-white">
                {t('eca.searchTermReport.reviewSearch.notes')}
              </Typography>
              <Typography className="typo-text body2_size details-value export-value-light">
                {exportDetails?.hasOwnProperty('notes')
                  ? `${exportDetails?.notes}`
                  : '- -'}
              </Typography>
            </Box>
          </Box>
          <Box className="setup-details-title">
            <Typography
              className="typo-text label2_size subhead-review-text export-light-blue"
              tabIndex={0}
            >
              {t('eca.newExport.fieldDetails')}
            </Typography>
          </Box>
          <Box className="fields-details">
            <Typography
              className="typo-text subhead4_size export-white"
              style={{width: '200px'}}
              tabIndex={0}
            >
              {t('eca.newExport.selectedField')}
            </Typography>
            <Box className="selected-fields scrollable-box" tabIndex={0}>
              {exportDetails?.exportCustomFields?.map((field, index) => {
                const matchedMeta = fieldData?.find(
                  (item) => item?.fieldLabel === field?.fieldLabel
                );

                return (
                  <Box
                    key={`${field?.fieldLabel}${index}`}
                    className="field-item"
                  >
                    <Typography className="typo-text body2_size details-value export-value-light field-label-section ">
                      {field?.fieldLabel}
                    </Typography>
                    <Typography className="typo-text body3_size details-value export-light-blue">
                      ({matchedMeta?.fieldCategoryLabel})
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Box>
      <AurLoadingIndicator overlay={true} isOpen={reqLoading} />
    </div>
  );
};

export default ExportViewDetails;
